import { createContext } from "react";
import { AccrualReviewApproveResponse } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { MatchableItem, MatchableTransaction } from "./types";

interface AccrualMatchContextValue {
    sourceTransaction: MatchableTransaction | null;
    selectedItems: MatchableItem[];
    addItem: (item: MatchableItem) => void;
    removeItem: (id: string) => void;
    selectedItemIds: Set<string>;
    /**
     * Ids already saved on the server when the drawer opened, so the footer
     * can tell a fresh edit from the persisted selection.
     */
    persistedItemIds: ReadonlySet<string>;
    currentTxLineItemMatchAmounts: ReadonlyMap<string, number>;
    /**
     * Accounting basis of the source transaction's entity. `cash` when the
     * entity has no accrual ledger (transfer pairing + invoice/bill matching
     * without Resolve difference), `accruals` when it does.
     */
    ledgerBasis: AccountingLedgerBasis;
    /** Convenience flag, true when `ledgerBasis === cash`. */
    isCashBasis: boolean;
    /**
     * Whether the workspace is GL-first. Drives the cash-basis GL column:
     * GL-first cash entities show a "Cash ledger" column, while non-GL-first
     * workspaces (which classify by category) show a "Category" column.
     */
    isGlFirst: boolean;
    onOpenEditAllocation?: (sourceItem: MatchableItem) => void;
    onConfirmSuccess?: (response: AccrualReviewApproveResponse) => void;
    /** Closes the host drawer; called after any successful save. */
    onClose?: () => void;
}

export const AccrualMatchCtx = createContext<AccrualMatchContextValue>({
    sourceTransaction: null,
    selectedItems: [],
    addItem: () => {},
    removeItem: () => {},
    selectedItemIds: new Set(),
    persistedItemIds: new Set(),
    currentTxLineItemMatchAmounts: new Map(),
    ledgerBasis: AccountingLedgerBasis.accruals,
    isCashBasis: false,
    isGlFirst: true,
});
